import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import { useNavbarUser } from '../../utils/navbarUser';
import { authApi, SPADES_UPDATED_EVENT } from '../../services/authApi';
import streamApi from '../../services/streamApi';
import PlatformPlanSection from './PlatformPlanSection';
import './SubscriptionsPage.css';

const SUBSCRIPTION_COST = 2500;

const PerkIcon = ({ children }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const SPADES_PERKS = [
  {
    icon: (
      <PerkIcon>
        <circle cx="12" cy="8" r="6" />
        <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
      </PerkIcon>
    ),
    title: 'Exclusive Badge',
    desc: 'Flex a subscriber badge next to your name and everyone will notice.',
  },
  {
    icon: (
      <PerkIcon>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </PerkIcon>
    ),
    title: 'Priority Chat',
    desc: 'Your messages rise to the top so the streamer always sees you.',
  },
  {
    icon: (
      <PerkIcon>
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
      </PerkIcon>
    ),
    title: 'First in Line',
    desc: "Your images and GIFs play before anyone else's, every time.",
  },
];

function SubscriptionsPage() {
  const user = useNavbarUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [planBanner, setPlanBanner] = useState(null);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [mySubscribers, setMySubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingFor, setCancellingFor] = useState('');
  const searchTimer = useRef(null);
  const activationRan = useRef(false);

  const myUserId = user?.id || null;

  const refresh = async () => {
    setLoading(true);
    try {
      const [subs, subers] = await Promise.all([
        streamApi.getMySubscriptions().catch(() => []),
        streamApi.getMySubscribers().catch(() => []),
      ]);
      setMySubscriptions(subs);
      setMySubscribers(subers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (activationRan.current) return;
    const params = new URLSearchParams(location.search);
    const plan = params.get('plan');
    const sessionId = params.get('session_id');
    if (!plan) return;
    activationRan.current = true;

    if (plan === 'success' && sessionId) {
      authApi
        .activatePlatformPlan(sessionId)
        .then(() => {
          setPlanBanner({ type: 'success', message: 'Discovery Plan activated — welcome to PRO!' });
        })
        .catch((err) => {
          setPlanBanner({
            type: 'error',
            message: err?.message || 'Could not activate plan. Contact support.',
          });
        });
    } else if (plan === 'cancelled') {
      setPlanBanner({ type: 'error', message: 'Checkout cancelled — no charge was made.' });
    }
    navigate({ pathname: location.pathname }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const results = await authApi.searchUsers(trimmed);
        const filtered = Array.isArray(results)
          ? results.filter((u) => u.id && u.id !== myUserId)
          : [];
        setSearchResults(filtered.slice(0, 8));
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [query, myUserId]);

  const activeSubscriptions = useMemo(
    () => mySubscriptions.filter((s) => s.status === 'active'),
    [mySubscriptions],
  );
  const pastSubscriptions = useMemo(
    () => mySubscriptions.filter((s) => s.status !== 'active'),
    [mySubscriptions],
  );

  const handleSelect = (candidate) => {
    setSelected(candidate);
    setQuery(candidate.username || '');
    setSearchResults([]);
    setError('');
  };

  const handleSubscribe = async () => {
    if (!selected?.id) {
      setError('Pick a streamer to subscribe to');
      return;
    }
    setSubscribing(true);
    setError('');
    setSuccess('');
    try {
      const result = await streamApi.subscribeToStreamer(selected.id);
      setSuccess(`Subscribed to ${selected.username}`);
      setSelected(null);
      setQuery('');
      if (typeof result?.new_balance === 'number' && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(SPADES_UPDATED_EVENT, {
            detail: { spadeBalance: result.new_balance },
          }),
        );
      }
      await refresh();
    } catch (err) {
      setError(err?.message || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async (streamerUserId) => {
    if (!streamerUserId) return;
    setCancellingFor(streamerUserId);
    setError('');
    setSuccess('');
    try {
      await streamApi.cancelSubscription(streamerUserId);
      setSuccess('Subscription cancelled');
      await refresh();
    } catch (err) {
      setError(err?.message || 'Failed to cancel subscription');
    } finally {
      setCancellingFor('');
    }
  };

  return (
    <div className="subscriptions-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={() => {}}
      />
      <LeftSidebar />
      <main className="subscriptions-page__main">
        <div className="subscriptions-page__content">
          <h1 className="subscriptions-page__title">Subscriptions</h1>
          <p className="subscriptions-page__intro">
            Two ways to grow: back a streamer with spades, or boost your own channel with the Discovery Plan.
          </p>

          {error && <div className="subscriptions-page__alert subscriptions-page__alert--error">{error}</div>}
          {success && <div className="subscriptions-page__alert subscriptions-page__alert--success">{success}</div>}

          <div className="subscriptions-page__cards-row">
            <section className="subscriptions-page__card subscriptions-page__card--spades">
              <div className="subscriptions-page__pro-header">
                <h2 className="subscriptions-page__pro-title">
                  <span className="subscriptions-page__pro-star" aria-hidden="true">♠</span>
                  Subscribe with spades
                </h2>
                <span className="subscriptions-page__pro-price">
                  {SUBSCRIPTION_COST.toLocaleString()} spades / 30 days
                </span>
              </div>
              <p className="subscriptions-page__pro-subtitle">
                Support a specific streamer each month. Your spades go straight to them.
              </p>
              <div className="subscriptions-page__pro-perks">
                <p className="subscriptions-page__pro-perks-label">What you get</p>
                <div className="subscriptions-page__pro-perks-grid">
                  {SPADES_PERKS.map((perk) => (
                    <div key={perk.title} className="subscriptions-page__pro-perk">
                      <span className="subscriptions-page__pro-perk-icon">{perk.icon}</span>
                      <div>
                        <p className="subscriptions-page__pro-perk-title">{perk.title}</p>
                        <p className="subscriptions-page__pro-perk-desc">{perk.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="subscriptions-page__search">
                <input
                  type="text"
                  className="subscriptions-page__input"
                  placeholder="Search streamer username"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null);
                  }}
                  autoComplete="off"
                />
                {searchResults.length > 0 && (
                  <ul className="subscriptions-page__results">
                    {searchResults.map((match) => (
                      <li key={match.id}>
                        <button
                          type="button"
                          className="subscriptions-page__result"
                          onClick={() => handleSelect(match)}
                        >
                          <UserAvatar
                            username={match.username}
                            avatarUrl={match.profileImage}
                          />
                          <span>{match.username}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="subscriptions-page__primary"
                onClick={handleSubscribe}
                disabled={!selected || subscribing}
              >
                {subscribing
                  ? 'Subscribing…'
                  : selected
                    ? `Subscribe to @${selected.username} (${SUBSCRIPTION_COST.toLocaleString()} spades)`
                    : 'Pick a streamer'}
              </button>
            </section>

            <PlatformPlanSection
              banner={planBanner}
              onBannerClear={() => setPlanBanner(null)}
            />
          </div>

          <section className="subscriptions-page__card">
            <h2 className="subscriptions-page__section-title">My subscriptions</h2>
            {!loading && activeSubscriptions.length > 0 && (
              <p className="subscriptions-page__spend">
                You&apos;re spending{' '}
                <strong>
                  {(activeSubscriptions.length * SUBSCRIPTION_COST).toLocaleString()} spades/month
                </strong>{' '}
                on {activeSubscriptions.length} subscription{activeSubscriptions.length === 1 ? '' : 's'}.
              </p>
            )}
            {loading ? (
              <p className="subscriptions-page__muted">Loading…</p>
            ) : activeSubscriptions.length === 0 ? (
              <p className="subscriptions-page__muted">You aren&apos;t subscribed to anyone yet.</p>
            ) : (
              <ul className="subscriptions-page__list">
                {activeSubscriptions.map((sub) => (
                  <li key={sub.streamer_user_id} className="subscriptions-page__row">
                    <div className="subscriptions-page__row-identity">
                      <UserAvatar
                        username={sub.streamer_username}
                        avatarUrl={sub.streamer_avatar_url}
                      />
                      <div>
                        <UsernameLink username={sub.streamer_username} />
                        <div className="subscriptions-page__row-meta">
                          {renewalLabel(sub.next_renewal_at)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="subscriptions-page__secondary"
                      onClick={() => handleCancel(sub.streamer_user_id)}
                      disabled={cancellingFor === sub.streamer_user_id}
                    >
                      {cancellingFor === sub.streamer_user_id ? 'Cancelling…' : 'Cancel'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {pastSubscriptions.length > 0 && (
              <details className="subscriptions-page__past">
                <summary>Past subscriptions ({pastSubscriptions.length})</summary>
                <ul className="subscriptions-page__list">
                  {pastSubscriptions.map((sub) => (
                    <li key={`${sub.streamer_user_id}-${sub.created_at}`} className="subscriptions-page__row">
                      <div className="subscriptions-page__row-identity">
                        <UserAvatar
                          username={sub.streamer_username}
                          avatarUrl={sub.streamer_avatar_url}
                        />
                        <div>
                          <UsernameLink username={sub.streamer_username} />
                          <div className="subscriptions-page__row-meta">
                            {sub.status} · started {formatDate(sub.created_at)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>

          <section className="subscriptions-page__card">
            <h2 className="subscriptions-page__section-title">
              My subscribers{!loading && mySubscribers.length > 0 ? ` (${mySubscribers.length})` : ''}
            </h2>
            {loading ? (
              <p className="subscriptions-page__muted">Loading…</p>
            ) : mySubscribers.length === 0 ? (
              <p className="subscriptions-page__muted">No active subscribers yet.</p>
            ) : (
              <ul className="subscriptions-page__list">
                {mySubscribers.map((sub) => (
                  <li key={sub.viewer_user_id} className="subscriptions-page__row">
                    <div className="subscriptions-page__row-identity">
                      <UserAvatar
                        username={sub.viewer_username}
                        avatarUrl={sub.viewer_avatar_url}
                      />
                      <div>
                        <UsernameLink username={sub.viewer_username} />
                        <div className="subscriptions-page__row-meta">
                          Since {formatDate(sub.created_at)}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <Footer />
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '—';
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function renewalLabel(iso) {
  const days = daysUntil(iso);
  if (days === null) return `Renews on ${formatDate(iso)}`;
  if (days <= 0) return 'Renews today';
  if (days === 1) return 'Renews in 1 day';
  return `Renews in ${days} days`;
}

function initialsOf(username) {
  const trimmed = String(username || '').trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, 2).toUpperCase();
}

function UserAvatar({ username, avatarUrl }) {
  if (avatarUrl) {
    return (
      <img
        className="subscriptions-page__avatar"
        src={avatarUrl}
        alt={username || 'user'}
        loading="lazy"
      />
    );
  }
  return (
    <div className="subscriptions-page__avatar subscriptions-page__avatar--fallback">
      {initialsOf(username)}
    </div>
  );
}

function UsernameLink({ username }) {
  if (!username) {
    return <div className="subscriptions-page__row-title">—</div>;
  }
  return (
    <Link
      to={`/profile/${encodeURIComponent(username)}`}
      className="subscriptions-page__row-title subscriptions-page__row-link"
    >
      {username}
    </Link>
  );
}

export default SubscriptionsPage;
